<template>
  <article
    class="credits-page"
    :class="{ 'credits-page--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="credits-title"
  >
    <div class="credits-layout">
      <aside class="credits-index">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>On this page</summary>
          <nav aria-label="Credits and gratitude sections">
            <a href="#credits-title">Credits &amp; gratitude</a>
            <a href="#full-credits">The collaboration</a>
            <a href="#pepe-moreno">Pepe Moreno</a>
            <a href="#sergio">Sergio</a>
            <a href="#jose">Jose</a>
            <a href="#everyone">To everyone who believed</a>
          </nav>
        </details>
      </aside>
      <div class="credits-body">
        <header>
          <p class="credits-eyebrow">
            About the people behind Self Biography
          </p>
          <h1 id="credits-title">
            Credits &amp; Gratitude
          </h1>
          <p class="credits-lead">
            <strong>The idea—and the people who make it work.</strong>
          </p>
          <p>Self Biography is the result of an ongoing collaboration between creative vision, design, and engineering.</p>
          <p>Ideas are discussed, implemented, tested, corrected, and improved together—often in real time.</p>
          <p>Our gratitude goes to everyone who has helped bring the idea this far.</p>
          <p class="credits-emphasis">
            <strong>You know who you are. Thank you.</strong>
          </p>
          <a
            class="credits-read-more"
            href="#full-credits"
          >Read more ↓</a>
        </header>
        <section
          class="credits-section"
          aria-labelledby="full-credits"
        >
          <p class="credits-eyebrow">
            Full credits &amp; gratitude
          </p>
          <h2 id="full-credits">
            The idea—and the people who make it work.
          </h2>
          <p>Self Biography began with an idea: finding a better way to preserve the memories, accomplishments, relationships, and experiences that make up a human life.</p>
          <p>But turning an idea into a working platform requires more than writing instructions and handing them to a programmer.</p>
          <p>Self Biography has developed through a highly cooperative process between concept, design, and engineering.</p>
          <p>Ideas are discussed, implemented, tested, corrected, refined, and expanded. New possibilities emerge while existing features are being developed.</p>
          <p class="credits-statement">
            <strong>Design influences engineering, and engineering influences design.</strong>
          </p>
          <section
            class="credits-person"
            aria-labelledby="pepe-moreno"
          >
            <h3 id="pepe-moreno">
              Pepe Moreno <span>— Designer-Creator</span>
            </h3>
            <p>Self Biography grew from Pepe Moreno's own need to organize and preserve a lifetime of memories, photographs, artwork, professional accomplishments, people, places, and experiences.</p>
            <p>As Designer-Creator, Pepe conceived the platform's central ideas, experience, and evolving features, working directly with the engineers who turn those concepts into functioning technology.</p>
            <p>His approach draws upon decades of visual storytelling, interactive entertainment, software design, and experimentation with emerging technologies.</p>
          </section>
          <section
            class="credits-person"
            aria-labelledby="sergio"
          >
            <h3 id="sergio">
              Sergio <span>— The Original Coder</span>
            </h3>
            <p>Sergio worked with Pepe to develop the original Self Biography platform, helping turn its early concepts and designs into functioning software and establishing an important technical foundation for the project.</p>
            <p>His contribution helped bring Self Biography from an idea into a working system.</p>
          </section>
          <section
            class="credits-person"
            aria-labelledby="jose"
          >
            <h3 id="jose">
              Jose <span>— Engineer &amp; Friend</span>
            </h3>
            <p>Jose's engineering work and close collaboration have become central to Self Biography's continuing development.</p>
            <p>Rather than following a conventional process of isolated specifications and long development cycles, ideas are explored and tested together.</p>
            <p>A feature can begin as a conversation, become a working implementation, and evolve through immediate testing, correction, and new discoveries.</p>
            <p>It is a partnership built on creative exchange, technical capability, friendship, and trust.</p>
          </section>
          <section
            class="credits-thanks"
            aria-labelledby="everyone"
          >
            <h3 id="everyone">
              To everyone who believed in the idea
            </h3>
            <p>To everyone who has shown an interest in Self Biography, encouraged us, offered suggestions, tested features, opened doors, or simply supported us during these early stages:</p>
            <p class="credits-emphasis">
              <strong>You know who you are. Thank you.</strong>
            </p>
            <p>Ideas don't grow in isolation. Sometimes a conversation, a suggestion, a criticism, or a simple encouragement makes all the difference.</p>
            <p>We are still at the beginning, and we are grateful to everyone who has helped us get here.</p>
            <p class="credits-emphasis">
              <strong>Thank you for believing in the idea.</strong>
            </p>
          </section>
        </section>
        <footer class="credits-links">
          <router-link to="/pages/self-biography">
            About Self Biography →
          </router-link>
          <router-link to="/pages/company">
            The company →
          </router-link>
          <a href="#credits-title">Back to top ↑</a>
        </footer>
      </div>
    </div>
  </article>
</template>
<script>
export default {
  name: 'SelfBiographyCreditsGratitude',
  data: () => ({ previousTitle: '' }),
  mounted() {
    this.previousTitle = document.title
    document.title = 'Credits & Gratitude | Self Biography'
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const target = ['credits-title', 'full-credits', 'pepe-moreno', 'sergio', 'jose', 'everyone'].includes(id) && document.getElementById(id)
      if (target) target.scrollIntoView()
      else window.scrollTo(0, 0)
    })
  },
  beforeDestroy() { document.title = this.previousTitle },
}
</script>
<style lang="scss" scoped>
.credits-page {
  --credits-text: #5e5669; --credits-heading: #393345; --credits-accent: #713ac7; --credits-surface: #fff; --credits-tint: #f3eefb; --credits-divider: rgba(94,86,105,.16);
  max-width: 1160px; margin: 24px auto 48px; color: var(--credits-text); font-size: 16px; line-height: 1.8; text-align: left;
  &--dark { --credits-text: #d0cadf; --credits-heading: #eee9fa; --credits-accent: #c6a5ff; --credits-surface: #312d4b; --credits-tint: #39304f; --credits-divider: rgba(231,227,252,.16); }
  h1,h2,h3 { color: var(--credits-heading); font-weight: 600; line-height: 1.35; text-align: left; scroll-margin-top: 88px; }
  h1 { font-size: clamp(2rem,3.7vw,3rem); letter-spacing: -.035em; margin-bottom: 24px; }
  h2 { font-size: clamp(1.4rem,2.3vw,1.8rem); letter-spacing: -.02em; margin-bottom: 22px; }
  h3 { font-size: 1.25rem; margin-bottom: 18px; }
  h3 span { color: var(--credits-accent); font-size: 1rem; }
  p { margin-bottom: 18px; }
  strong { font-weight: 600; }
  a { color: var(--credits-accent); text-underline-offset: 4px; }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--credits-accent); outline-offset: 4px; }
  .credits-eyebrow { color: var(--credits-accent); font-size: .75rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 14px; }
}
.credits-layout { display: grid; grid-template-columns: 200px minmax(0,1fr); gap: 32px; align-items: start; }
.credits-index { position: sticky; top: 88px; }
.credits-index summary { font-weight: 600; cursor: pointer; min-height: 44px; }
.credits-index nav { display: flex; flex-direction: column; border-left: 1px solid var(--credits-divider); padding-left: 16px; }
.credits-index a { display: flex; align-items: center; min-height: 44px; padding: 6px 0; font-size: .875rem; line-height: 1.5; text-decoration: none; }
.credits-index a:hover { text-decoration: underline; }
.credits-body { min-width: 0; background: var(--credits-surface); border: 1px solid var(--credits-divider); border-radius: 8px; padding: 44px; }
.credits-lead { font-size: 1.2rem; color: var(--credits-heading); }
.credits-emphasis { color: var(--credits-accent); }
.credits-read-more { display: inline-flex; align-items: center; min-height: 44px; font-weight: 600; }
.credits-section { margin-top: 30px; padding-top: 34px; border-top: 1px solid var(--credits-divider); }
.credits-statement { color: var(--credits-accent); padding: 20px 24px; background: var(--credits-tint); border-radius: 6px; margin: 24px 0; }
.credits-person { margin-top: 30px; padding-top: 28px; border-top: 1px solid var(--credits-divider); }
.credits-person > :last-child { margin-bottom: 0; }
.credits-thanks { margin-top: 36px; padding: 28px; background: var(--credits-tint); border-radius: 6px; }
.credits-thanks > :last-child { margin-bottom: 0; }
.credits-links { display: flex; flex-wrap: wrap; gap: 12px 24px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--credits-divider); }
.credits-links a { display: inline-flex; align-items: center; min-height: 44px; font-size: .875rem; }
@media(max-width:959px) {
  .credits-layout { grid-template-columns: minmax(0,1fr); gap: 20px; }
  .credits-index { position: static; }
  .credits-index summary { padding: 8px 0; }
  .credits-body { padding: 28px; }
}
@media(max-width:599px) {
  .credits-page { margin: 0 auto 32px; }
  .credits-body { padding: 24px 0 0; border: 0; border-top: 1px solid var(--credits-divider); border-radius: 0; background: transparent; }
  .credits-thanks { padding: 20px; }
  .credits-statement { padding: 18px 20px; }
  .credits-links { flex-direction: column; gap: 4px; }
}
</style>
